<script setup lang="ts">
    import { NotificationContainer, useToasteo } from '@deegital/vue-trustup-io-toasteo';
    import { storeToRefs } from 'pinia';
    import { breakpointsTailwind, useBreakpoints, useElementSize } from '@vueuse/core';
    import { ModelType } from '~/app/base/schemas/BaseSchema';
    import { CONFIGURATOR } from '~/app/common/enums/Configurator';
    import type ConfiguratorType from '~/app/common/models/Configurator';
    import EndpointFactory from '~/app/factories/EndpointFactory';
    import { useDocumentQuotaStore } from '~/stores/documentQuotaStore';
    const toasteo = useToasteo();
    const translation = useTranslation();

    const { isLoading, loading, load } = useLoadingScreen(useRoute());
    const { tenant, authed, isImpersonating } = storeToRefs(useAuthStore());
    const { currentModule, appsModules, extraModules } = useModules();
    const { hasReferralAccess } = useReferral();

    // Boot the CometChat session app-wide and keep the sidebar "Messages" unread badge (AC9)
    // live on every page. No-ops for tenants without a cometchatAuthToken (Phase 5 / TDM-587).
    useMessagesUnreadInit();

    useProfileReviewInit();

    // Disabled for the moment
    // const sidebarCollapsed = ref(false);
    // const toggleSidebarCollapsed = () => {
    //     sidebarCollapsed.value = !sidebarCollapsed.value;
    //     localStorage.setItem('sidebarCollapsed', sidebarCollapsed.value.toString());
    // };

    // const testModeHeight = computed(() => (tenant.value?.isInTestMode ? '60px' : '0px'));

    // const showIntercom = () => {
    //     window.Intercom('showNewMessage');
    // };

    const documentConfigurator = ref<ConfiguratorType | null>();
    const configuratorEndpoint = EndpointFactory.make(ModelType.CONFIGURATORS);
    const getDocumentConfigurator = async () => {
        if (authed.value?.role !== 'super-admin') {
            return;
        }

        const configuratorResponse = await configuratorEndpoint.index({
            includeMeta: 'progressBar,form',
            filter: {
                key: CONFIGURATOR.DOCUMENT,
            },
        });

        if (configuratorResponse.error) {
            return;
        }

        documentConfigurator.value = configuratorResponse.data?.first();
    };

    // Handling mulltiple sidebar
    const mainSidebarCollapsed = ref(false);
    // const moduleSidebarCollapsed = ref(false);

    // Split function for sidebars — uncomment when collapse feature is implemented
    // const toggleMainSidebar = () => {
    //     mainSidebarCollapsed.value = !mainSidebarCollapsed.value;
    //     localStorage.setItem('mainSidebarCollapsed', mainSidebarCollapsed.value.toString());
    // };

    const breakpoints = useBreakpoints(breakpointsTailwind);

    const isMobile = breakpoints.smaller('lg');

    // Mobile: hide the Intercom floating launcher — the "Support" entry in the
    // burger menu replaces it (opens the conversation via useIntercom). Desktop:
    // ensure it's shown again.
    watch(isMobile, (mobile) => useIntercomUpdate({ hide_default_launcher: mobile }), { immediate: true });

    // Screens that provide their own sidebar slot (e.g. sidebarLess layout) don't show banners
    const slots = useSlots();
    const hasCustomSidebar = computed(() => !!slots.sidebar);
    // Banner-level priority (TDD-5606): unpaid L4 shows no banner (the blocking screen
    // takes over) and also wins over non-renewing. Cancelled/paused never reach this
    // layout (the 403 flow redirects to their dedicated screens). Remaining banners stack.
    const { showBanner: showUnpaidBanner, isBlocked: isUnpaidBlocked } = useUnpaidStatus();
    const showNonRenewingBanner = computed(() => (!!tenant.value?.isNonRenewing || isNonRenewingForced()) && !isUnpaidBlocked.value);
    const showBanners = computed(() => !hasCustomSidebar.value && (!!tenant.value?.isInTestMode || showUnpaidBanner.value || showNonRenewingBanner.value));

    // Total stacked banners height drives the fixed mobile offsets (header, concave corners, content padding)
    const bannersRef = ref<HTMLElement | null>(null);
    const { height: bannersHeight } = useElementSize(bannersRef);
    watchEffect(() => {
        document.documentElement.style.setProperty('--demo-banner-height', `${Math.round(bannersHeight.value)}px`);
    });
    const ui = useUiStore();

    const isActive = ref(false);
    const route = useRoute();

    // Reset the main scroll position on navigation so a new page never opens
    // already scrolled down (TDD-3412). Watches the path only, so query-param
    // changes (tabs, filters) don't jump the scroll.
    const mainRef = ref<HTMLElement | null>(null);
    watch(
        () => route.path,
        () => {
            nextTick(() => mainRef.value?.scrollTo({ top: 0 }));
        }
    );
    // Arrivée initiale (post-login) : le watcher de path ne se déclenche pas au
    // premier rendu et le contenu pouvait apparaître déjà scrollé (TDD-3555).
    // On re-cale le scroll quand l'écran de chargement se retire.
    watch(isLoading, (value) => {
        if (!value) nextTick(() => mainRef.value?.scrollTo({ top: 0 }));
    });

    const toggle = () => {
        isActive.value ? hideMenu() : showMenu();
    };

    const showMenu = () => {
        ui.toggleScroll(false);
        ui.hasTransition = 'menu';
        isActive.value = true;
    };
    const hideMenu = () => {
        ui.toggleScroll(true);
        ui.hasTransition = 'none';
        isActive.value = false;
    };

    // Close the mobile burger menu whenever the route changes (link click / navigation)
    watch(
        () => route.fullPath,
        () => {
            if (isActive.value) hideMenu();
        }
    );

    // Measure the stable top region of the fixed mobile header (app banner + header bar)
    // and publish its height so the content area and concave corners offset themselves below it.
    const mobileHeaderRef = ref<HTMLElement | null>(null);
    const { height: mobileHeaderHeight } = useElementSize(mobileHeaderRef, undefined, { box: 'border-box' });

    watchEffect(() => {
        if (typeof document === 'undefined') return;

        const value = isMobile.value && mobileHeaderHeight.value > 0 ? `${mobileHeaderHeight.value}px` : '0px';
        document.documentElement.style.setProperty('--mobile-header-height', value);
    });

    const sidebarNavRef = ref<HTMLElement | null>(null);
    const canScrollDown = ref(false);

    const updateScrollIndicator = () => {
        const el = sidebarNavRef.value;
        if (!el) return;
        canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    };

    const configuratorRoutes = [
        'invoicing-invoices',
        'invoicing-quotes',
        'invoicing-credit-notes',
        'invoicing-delivery-slips',
        'invoicing-order-slips',
        // add more routes
    ];

    const documentQuotaStore = useDocumentQuotaStore();
    useListen('invoicing:invoice:created' as keyof ApplicationEvents, () => documentQuotaStore.refresh('documents'));
    useListen('invoicing:invoice:deleted' as keyof ApplicationEvents, () => documentQuotaStore.refresh('documents'));
    useListen('invoicing:expense:created' as keyof ApplicationEvents, () => documentQuotaStore.refresh('expenses'));

    onMounted(async () => {
        // Get sidebar status
        if (localStorage.getItem('mainSidebarCollapsed') === 'true') {
            mainSidebarCollapsed.value = true;
        }
        // if (localStorage.getItem('moduleSidebarCollapsed') === 'true') {
        //     moduleSidebarCollapsed.value = true;
        // }

        if (load) {
            await load();
        }

        if (authed.value && tenant.value?.shouldAcceptTermsOfService(authed.value as any)) {
            useEvent('common:terms-of-service:accept-modal:open' as keyof ApplicationEvents, (tenant.value as any)?.meta?.termsOfService?.id);
        }

        getDocumentConfigurator();
        documentQuotaStore.load();

        nextTick(() => updateScrollIndicator());
    });
</script>

<template>
    <div
        :key="translation.getCurrentLocale()"
        class="relative flex flex-col app-viewport-height"
    >
        <transition
            leave-active-class="duration-300 ease-in"
            leave-from-class="opacity-100 blur-none"
            leave-to-class="opacity-0 blur-xl"
        >
            <LayoutLoadingScreen
                v-if="isLoading"
                :loading="loading"
            />
        </transition>
        <template v-if="!isLoading">
            <!-- Impersonate indicator: fixed frame above everything, no layout impact -->
            <div
                v-if="isImpersonating"
                class="fixed inset-0 z-[9999] pointer-events-none border-[5px] border-branding-brand-yellow"
            >
                <div class="absolute top-0 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-branding-brand-yellow text-black rounded-b-md px-3 py-1">
                    <i class="fa-solid fa-masks-theater"></i>
                    <h2 class="text-xs font-[Inter] font-bold whitespace-nowrap">Attention : vous êtes en impersonate</h2>
                </div>
            </div>
            <header
                v-if="isMobile"
                class="fixed z-[100] overflow-hidden transition-[height] duration-300 ease-in-out"
                :class="[$theme('layout.header'), isActive ? 'h-screen bg-sidebar-gradient overflow-y-auto' : 'h-auto bg-sidebar-gradient']"
                :style="{ top: 'var(--demo-banner-height, 0px)' }"
            >
                <!-- Stable top region (app banner + header bar) — measured for content/concave offset -->
                <div ref="mobileHeaderRef">
                    <client-only>
                        <UiMobileAppBanner />
                    </client-only>
                    <div class="mobile-header-bar relative flex flex-col px-5 py-[14px] w-full">
                        <div class="flex items-center justify-between">
                            <!-- Tenant logo -->
                            <NuxtLink
                                href="/"
                                class="flex-shrink-0 w-[36px] h-[36px] rounded-md bg-neutral-100 bg-cover bg-center"
                                :class="{ 'bg-neutral-200': !tenant?.logoUrl }"
                                :style="tenant?.logoUrl ? `background-image: url(${tenant.logoUrl})` : ''"
                            >
                                <span
                                    v-if="!tenant?.logoUrl"
                                    class="flex items-center justify-center w-full h-full text-xs font-semibold text-neutral-500"
                                >
                                    {{ tenant?.name?.charAt(0)?.toUpperCase() }}
                                </span>
                            </NuxtLink>

                            <!-- Trustup Pro logo (centered) -->
                            <NuxtLink
                                href="/"
                                class="absolute left-1/2 -translate-x-1/2"
                            >
                                <UiLogo class="sidebar-logo" />
                            </NuxtLink>

                            <!-- Burger menu -->
                            <button
                                class="relative z-10 flex items-center text-white bg-transparent border-0 cursor-pointer duration-300 transition-all ease-out"
                                aria-label="Mega menu trigger"
                                title="Mega menu trigger"
                                @click="toggle"
                            >
                                <div
                                    :class="isActive ? 'gap-[3px] is-active' : ''"
                                    class="flex flex-col items-center justify-center gap-2"
                                >
                                    <span class="mega-menu__trigger__span block w-[24px] h-[2px] origin-center bg-white will-change-auto duration-300 transition-all ease-out"></span>
                                    <span class="mega-menu__trigger__span block w-[24px] h-[2px] origin-center bg-white will-change-auto duration-300 transition-all ease-out"></span>
                                    <span class="mega-menu__trigger__span block w-[24px] h-[2px] origin-center bg-white will-change-auto duration-300 transition-all ease-out"></span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
                <transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 translate-y-[-10px]"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 translate-y-[-10px]"
                >
                    <div v-if="isActive">
                        <LayoutSidebarModules
                            :modules="appsModules"
                            :collapsed="false"
                            show-assistant
                            mobile-expand
                        />
                        <div class="border-t border-white/10 mt-2 pt-2">
                            <LayoutSidebarModules
                                :modules="extraModules"
                                :collapsed="false"
                                mobile-expand
                            />
                            <ul class="flex flex-col px-2 pb-3 gap-[2px]">
                                <li v-if="hasReferralAccess">
                                    <LayoutSidebarReferral :collapsed="false" />
                                </li>
                                <!-- <li>
                                    <LayoutSidebarNotifications :collapsed="false" />
                                </li> -->
                                <!-- Support (mobile only): replaces the Intercom launcher hidden on mobile -->
                                <li>
                                    <LayoutSidebarSupport :collapsed="false" />
                                </li>
                                <li>
                                    <LayoutSidebarAuthed
                                        :is-sidebar="true"
                                        :collapsed="false"
                                    />
                                </li>
                            </ul>
                        </div>
                    </div>
                </transition>
            </header>

            <div class="flex items-stretch flex-1 min-h-0 bg-sidebar-gradient">
                <slot name="sidebar">
                    <!-- z-[800] : sous les modales (z 900+ via useModalsStore) pour que leur overlay couvre aussi le menu -->
                    <aside
                        v-if="!isMobile"
                        class="flex-shrink-0 bg-sidebar-gradient dark:bg-neutral-900 transition-all duration-300 ease-in-out flex z-[800] flex-col relative group/main-sidebar overflow-visible"
                        :class="[mainSidebarCollapsed ? 'w-[60px] is-collapsed' : 'w-[200px]']"
                    >
                        <!-- Sidebar Toggle button — hidden until collapse feature is implemented -->
                        <!-- <button
                            class="absolute h-[80px] z-[99] right-0 translate-x-1/2 items-center hidden lg:group-hover/main-sidebar:flex"
                            @click.prevent="toggleMainSidebar"
                        >
                            <div
                                class="h-[20px] w-[20px] bg-white border shadow-2xl flex-shrink-0 rounded-full flex items-center justify-center transition-all hover:bg-neutral-100 text-gray-600 hover:text-black cursor-pointer"
                                :data-tooltip="mainSidebarCollapsed ? $t('actions.expand_sidebar') : $t('actions.collapse_sidebar')"
                                :data-tooltip-location="'right'"
                            >
                                <i
                                    class="text-xs fa-solid"
                                    :class="mainSidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"
                                ></i>
                            </div>
                        </button> -->

                        <!-- HEADER (fixed) -->
                        <div
                            class="flex-shrink-0 border-b border-[#455474] h-[58px] flex items-center justify-center"
                            :class="[mainSidebarCollapsed ? 'px-2' : 'px-6']"
                        >
                            <NuxtLink
                                href="/"
                                class="block"
                            >
                                <UiLogo
                                    class="sidebar-logo"
                                    :icon-only="mainSidebarCollapsed"
                                />
                            </NuxtLink>
                        </div>
                        <LayoutSidebarSearch :collapsed="mainSidebarCollapsed" />

                        <!-- NAV (scrollable) -->
                        <nav
                            ref="sidebarNavRef"
                            class="flex-1 min-h-0 overflow-y-auto nice-scrollbar [scrollbar-gutter:stable]"
                            @scroll="updateScrollIndicator"
                        >
                            <LayoutSidebarModules
                                :modules="appsModules"
                                :collapsed="mainSidebarCollapsed"
                                show-assistant
                            />
                        </nav>
                        <div
                            class="pointer-events-none h-8 -mt-8 relative z-10 transition-opacity duration-300 flex-shrink-0 bg-sidebar-scroll-fade"
                            :class="canScrollDown ? 'opacity-100' : 'opacity-0'"
                        />

                        <!-- BOTTOM (fixed, no overflow hidden) -->
                        <div class="flex-shrink-0 border-t border-[#455474] pt-2 pb-4">
                            <LayoutSidebarModules
                                :modules="extraModules"
                                :collapsed="mainSidebarCollapsed"
                            />
                            <ul class="flex flex-col px-2 pb-1 gap-[2px]">
                                <li v-if="hasReferralAccess">
                                    <LayoutSidebarReferral :collapsed="mainSidebarCollapsed" />
                                </li>
                                <!-- <li>
                                    <LayoutSidebarNotifications :collapsed="mainSidebarCollapsed" />
                                </li> -->
                                <li>
                                    <LayoutSidebarAuthed
                                        :is-sidebar="true"
                                        :collapsed="mainSidebarCollapsed"
                                    />
                                </li>
                            </ul>
                            <InvoicingDocumentQuotaBox
                                variant="nav"
                                class="document-quota-nav"
                            />
                            <!-- LayoutSidebarUpgradeCta hidden — will be re-enabled with subscription page rework -->
                        </div>
                    </aside>
                </slot>
                <div class="flex flex-col flex-1 min-h-0 w-[calc(100vw-200px)] md:rounded-l-[15px] md:overflow-hidden">
                    <!-- In flow on desktop so banners stack and are clipped under the rounded corner; fixed on mobile.
                         `max-lg`, not `max-md`: "mobile" here is the mobile chrome above (`isMobile`, `< lg`), and
                         the two must agree. In flow under a header that is `fixed` anyway, the banner pushed `main`
                         down without shortening it, so `main` overflowed past the viewport by the banner's height
                         and the parent's `md:overflow-hidden` cut that much off its bottom. -->
                    <div
                        v-if="showBanners"
                        ref="bannersRef"
                        class="flex-shrink-0 max-lg:fixed max-lg:top-0 max-lg:left-0 max-lg:right-0 max-lg:z-[110]"
                    >
                        <PageTestModeAlert
                            v-if="tenant && tenant.isInTestMode"
                            :tenant="tenant"
                        />
                        <PageUnpaidAlert v-if="showUnpaidBanner" />
                        <PageNonRenewingAlert v-if="showNonRenewingBanner" />
                    </div>
                    <main
                        ref="mainRef"
                        :data-template="route.name"
                        :current-module="currentModule?.key"
                        class="flex-1 overflow-y-auto overscroll-contain bg-white dark:bg-neutral-800 nice-scrollbar [scrollbar-gutter:stable] max-lg:pt-header bg-app-gradient"
                        :class="[showBanners ? 'h-inner-content-test-mode' : 'h-inner-content', isMobile ? 'mobile-header-concave' : '']"
                    >
                        <!-- Wrapper carries the mobile bottom spacing so the scrollable
                             content actually grows past the Intercom launcher (TDD-3412).
                             `md:h-full` restores a DEFINITE height on desktop so a page whose
                             root is `h-full` (e.g. Messages full-height shell) resolves against
                             it — `min-h-full` alone is auto-height and collapses such pages
                             (TDM-586). Mobile keeps `min-h-full` so tall content still grows past
                             the launcher; desktop never needs that bottom grow.
                             Stays on `md` while the chrome around it moved to `lg`: the launcher
                             this reserve is for is hidden below `lg`, and the tablet band needs
                             the definite height more than the reserve — `min-h-full` alone
                             collapses the pages TDM-586 is about. -->
                        <div class="min-h-full md:h-full max-md:pb-16">
                            <slot> </slot>
                        </div>
                    </main>
                </div>
            </div>
            <NotificationContainer
                class="notification-container"
                :toasteo="toasteo"
            />

            <!-- Client-only might be useless since SSR is false in the nuxt.config.ts -->
            <client-only>
                <LayoutModals />
            </client-only>
            <slot name="configurator">
                <client-only>
                    <ConfiguratorDocumentPopUp
                        v-if="documentConfigurator && !documentConfigurator.isCompleted() && configuratorRoutes.includes(route.name as string)"
                        :configurator="documentConfigurator"
                    />
                </client-only>
            </slot>
        </template>
    </div>
</template>

<style lang="scss">
    .h-content {
        height: calc(100vh);
        height: calc(100dvh);
    }
    .w-content {
        width: calc(100vw);
    }

    /* Hauteur de viewport dynamique (TDD-3412 #14b / TDD-3555) : `100dvh` suit les
     * barres dynamiques des navigateurs mobiles, là où `100vh` les ignore et
     * faisait "buter" le scroll avant la fin. La déclaration `100vh` juste avant
     * sert de fallback pour les navigateurs sans support dvh. */
    .app-viewport-height {
        height: 100vh;
        height: 100dvh;
    }

    /* App shell scroll lock (TDD-3751): make `main` the only scroll zone.
     * html/body stayed scrollable by a few pixels on iOS Safari, and swipes on
     * `fixed` chrome cannot scroll `main` (WebKit renders it in a viewport
     * anchored layer), so they scrolled the document and shifted the whole shell
     * under the header instead. Locked on html AND body, since html alone is not
     * enough on iOS. Keyed off `.app-viewport-height`, which only exists on this
     * layout's root, so the auth/blank/empty layouts keep the document scroll
     * their inbound/onboarding flows rely on. Capped at <1024px, where the
     * offending fixed chrome lives. */
    @media (max-width: 1023px) {
        html:has(.app-viewport-height),
        html:has(.app-viewport-height) body {
            overflow: hidden;
            overscroll-behavior: none;
        }
    }

    .h-inner-content {
        height: 100%;
    }

    .h-inner-content-test-mode {
        /* Banner is in flow above the main content on desktop */
        height: calc(100% - var(--demo-banner-height, 60px));
    }

    /* Capped at <1024px, where the mobile header is rendered (`isMobile`), not at <768px:
     * below 1024 the header is `fixed`, so without this padding the top of every page sat
     * under it, and `main` kept a height computed as if nothing overlapped it. */
    @media (max-width: 1023px) {
        /* On mobile the header is fixed and may include the app download banner, so its height is dynamic.
         * --mobile-header-height is measured live (see base.vue script) and includes the banner when shown.
         * Higher specificity (main.h-inner-content*) is required to win over the generic max-lg:pt-header utility. */
        main.h-inner-content-test-mode {
            padding-top: calc(var(--demo-banner-height, 60px) + var(--mobile-header-height, 74px));
            height: calc(100vh - var(--demo-banner-height, 60px) - var(--mobile-header-height, 74px));
            height: calc(100dvh - var(--demo-banner-height, 60px) - var(--mobile-header-height, 74px));
        }
        main.h-inner-content {
            padding-top: var(--mobile-header-height, 74px);
        }
    }

    @media (min-width: 768px) {
        .w-content {
            width: calc(100vw - 60px);
        }
        .md-h-inner-content {
            height: calc(100vh);
            height: calc(100dvh);
        }
        .md-h-inner-content-test-mode {
            height: calc(100vh - var(--demo-banner-height, 60px));
            height: calc(100dvh - var(--demo-banner-height, 60px));
        }
    }

    .mega-menu__trigger__span {
        .is-active & {
            transform: translateY(5px) rotate(45deg);

            &:nth-child(2) {
                opacity: 0;
            }

            &:last-child {
                transform: translateY(-5px) rotate(-45deg);
            }
        }
    }

    /* Concave corners below the mobile header.
     * Anchored to the real header bottom (demo banner + dynamic header height incl. app banner).
     * Same <1024px cap as the header they hang from — the class is already put on `main` by
     * `isMobile`, so under 768px only they were simply missing from the tablet band. */
    @media (max-width: 1023px) {
        .mobile-header-concave::before,
        .mobile-header-concave::after {
            content: '';
            position: fixed;
            top: calc(var(--demo-banner-height, 0px) + var(--mobile-header-height, 62.5px));
            width: 36px;
            height: 24px;
            background: #101b29;
            z-index: 99;
            pointer-events: none;
        }

        .mobile-header-concave::before {
            left: 0;
            clip-path: path('M36,0 L0,0 L0,24 C0,20 0,0 36,0 Z');
        }

        .mobile-header-concave::after {
            right: 0;
            clip-path: path('M0,0 L36,0 L36,24 C36,20 36,0 0,0 Z');
        }
    }

    .header-logo {
        .typo {
            fill: black;
        }
    }

    .sidebar-logo {
        .typo {
            fill: #fff;
        }
    }

    .document-quota-nav {
        display: none;
    }

    @media (min-height: 800px) {
        .document-quota-nav {
            display: block;
        }
    }
</style>
